import { createApi } from "@reduxjs/toolkit/query/react";

import { getBaseQuery } from "../baseQuery";
import type {
  ClientCredentialDTO,
  ClientDTO,
  CreateClientCredentialRequest,
  CreateClientCredentialResponse,
  CreateClientRequest,
} from "../../../types/ClientCredential";

export const clientCredentialsService = createApi({
  reducerPath: "clientCredentialsService",
  baseQuery: getBaseQuery("aiakos"),
  tagTypes: ["ClientCredential", "Client"],
  endpoints: (builder) => ({
    /**
     * Get all clients for dropdown
     * Calls aiakos: GET /api/clients
     */
    getClients: builder.query<ClientDTO[], void>({
      query: () => "api/clients",
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: "Client" as const, id })),
              { type: "Client", id: "LIST" },
            ]
          : [{ type: "Client", id: "LIST" }],
    }),

    /**
     * Get all client credentials
     * Calls aiakos: GET /api/credential/clients
     */
    getClientCredentials: builder.query<ClientCredentialDTO[], void>({
      query: () => "api/credential/clients",
      providesTags: [{ type: "ClientCredential", id: "LIST" }],
    }),

    /**
     * Get single client credential by ID
     * Calls aiakos: GET /api/credential/clients/:id
     */
    getClientCredential: builder.query<ClientCredentialDTO, number>({
      query: (id) => `api/credential/clients/${id}`,
      providesTags: (_result, _error, id) => [{ type: "ClientCredential", id }],
    }),

    /**
     * Create a new client credential
     * Calls aiakos: POST /api/credential/clients
     * Note: Secret is only returned in the response, never again
     */
    createClientCredential: builder.mutation<
      CreateClientCredentialResponse,
      CreateClientCredentialRequest
    >({
      query: (credential) => ({
        url: "api/credential/clients",
        method: "POST",
        body: credential,
      }),
      invalidatesTags: [{ type: "ClientCredential", id: "LIST" }],
    }),

    /**
     * Delete a client credential
     * Calls aiakos: DELETE /api/credential/clients/:id
     */
    deleteClientCredential: builder.mutation<void, number>({
      query: (id) => ({
        url: `api/credential/clients/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "ClientCredential", id: "LIST" }],
    }),

    /**
     * Create a new client
     * Calls aiakos: POST /api/clients
     */
    createClient: builder.mutation<ClientDTO, CreateClientRequest>({
      query: (client) => ({
        url: "api/clients",
        method: "POST",
        body: client,
      }),
      invalidatesTags: [{ type: "Client", id: "LIST" }],
    }),

    /**
     * Update a client
     * Calls aiakos: PUT /api/clients/:id
     * Uses optimistic updates to prevent scroll reset and provide instant feedback
     */
    updateClient: builder.mutation<ClientDTO, ClientDTO>({
      query: (client) => ({
        url: `api/clients/${client.id}`,
        method: "PUT",
        body: client,
      }),
      async onQueryStarted(updatedClient, { dispatch, queryFulfilled }) {
        // Optimistically update the cache
        const patchResult = dispatch(
          clientCredentialsService.util.updateQueryData(
            "getClients",
            undefined,
            (draft) => {
              const client = draft.find((c) => c.id === updatedClient.id);
              if (client) {
                Object.assign(client, updatedClient);
              }
            },
          ),
        );

        try {
          const { data } = await queryFulfilled;
          // Merge server response to preserve object reference (prevents scroll reset)
          dispatch(
            clientCredentialsService.util.updateQueryData(
              "getClients",
              undefined,
              (draft) => {
                const client = draft.find((c) => c.id === data.id);
                if (client) {
                  Object.assign(client, data);
                }
              },
            ),
          );
        } catch {
          // Rollback optimistic update on error
          patchResult.undo();
        }
      },
    }),

    /**
     * Delete a client
     * Calls aiakos: DELETE /api/clients/:id
     */
    deleteClient: builder.mutation<void, number>({
      query: (id) => ({
        url: `api/clients/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "Client", id: "LIST" }],
    }),
  }),
});

export const {
  useGetClientsQuery,
  useGetClientCredentialsQuery,
  useGetClientCredentialQuery,
  useCreateClientCredentialMutation,
  useDeleteClientCredentialMutation,
  useCreateClientMutation,
  useUpdateClientMutation,
  useDeleteClientMutation,
} = clientCredentialsService;
